import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Bell, Settings2, X, ChevronRight, Calendar, RotateCw } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { format, differenceInDays, parseISO } from 'date-fns';
import { calculateAnniversaries } from '../../utils/dateUtils';

const HomeScreen: React.FC = () => {
  const { coupleInfo, setCoupleInfo, dDayMode, setDDayMode, user, coupleId } = useAppContext();
  const [isEditing, setIsEditing] = useState(false);
  const [showAllAnniversaries, setShowAllAnniversaries] = useState(false);
  const [tempInfo, setTempInfo] = useState(coupleInfo);
  
  // Sync tempInfo when modal opens
  useEffect(() => {
    if (isEditing) {
      setTempInfo(coupleInfo);
    }
  }, [isEditing, coupleInfo]);

  // Refetch data on mount to ensure fresh status especially on mobile navigation
  useEffect(() => {
    // Only fetch if context isn't already loading
    console.log('[HomeScreen] Component mounted. Ensuring fresh data...');
  }, []);

  const isSoloMode = useMemo(() => {
    return !coupleId || (user && coupleId === user.id);
  }, [coupleId, user]);

  const activeDate = useMemo(() => {
    return dDayMode === 'met' ? (coupleInfo.firstMetDate || coupleInfo.anniversaryDate) : coupleInfo.anniversaryDate;
  }, [dDayMode, coupleInfo.firstMetDate, coupleInfo.anniversaryDate]);

  const diffDays = useMemo(() => {
    try {
      const start = parseISO(activeDate);
      const today = new Date();
      return differenceInDays(today, start) + 1;
    } catch (e) {
      return 0;
    }
  }, [activeDate]);

  const handleToggleMode = () => {
    const newMode = dDayMode === 'met' ? 'dated' : 'met';
    setDDayMode(newMode);
    localStorage.setItem('dDayMode', newMode);
  };

  const handleSave = async () => {
    console.log('[HomeScreen] Save button clicked. Info:', tempInfo);
    try {
      await setCoupleInfo(tempInfo);
      alert('설정이 성공적으로 저장되었습니다.');
      setIsEditing(false);
    } catch (error: any) {
      console.error('[HomeScreen] Save failed:', error);
      alert(error.message || '저장에 실패했습니다.');
    }
  };

  const allAnniversaries = useMemo(() => {
    return calculateAnniversaries(activeDate);
  }, [activeDate]);

  const upcomingAnniversaries = useMemo(() => {
    return allAnniversaries.slice(0, 2);
  }, [allAnniversaries]);

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Header */}
      <header className="flex justify-between items-center py-4">
        <div className="flex flex-col text-left">
          <h1 className="text-2xl font-bold text-charcoal">
            {coupleInfo.name1} & {coupleInfo.name2}
          </h1>
          {isSoloMode && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-black text-primary uppercase tracking-wider">혼자 기록 중</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setIsEditing(true)}
            className="p-2 bg-white rounded-full shadow-sm text-gray-500 hover:text-primary transition-colors cursor-pointer"
          >
            <Settings2 size={20} />
          </button>
          <button className="p-2 bg-white rounded-full shadow-sm text-gray-500">
            <Bell size={20} />
          </button>
        </div>
      </header>

      {/* D-Day Counter */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={handleToggleMode}
        className="relative h-80 rounded-3xl overflow-hidden shadow-lg cursor-pointer group"
      >
        <img 
          src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=800&auto=format&fit=crop" 
          alt="Couple background"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white p-6 backdrop-blur-[1px]">
          <motion.div
            key={dDayMode}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center"
          >
            <Heart className="text-primary mb-2 fill-primary" size={40} />
            <span className="text-lg font-medium opacity-90">
              우리 {dDayMode === 'met' ? '만난 지' : '사랑한 지'}
            </span>
            <h2 className="text-6xl font-black mt-2 tracking-tight">{diffDays}일</h2>
            <span className="mt-4 bg-white/20 backdrop-blur-md px-4 py-1 rounded-full text-sm font-medium">
              {activeDate.replace(/-/g, '.')} ~
            </span>
          </motion.div>
          
          <div className="absolute bottom-6 flex items-center gap-1 text-[10px] font-bold opacity-60">
            <RotateCw size={10} /> 탭하여 전환
          </div>
        </div>
      </motion.div>

      {/* Upcoming Anniversary Banner - Top 2 Only */}
      <section>
        <div className="flex items-center justify-between mb-4 px-1">
          <h3 className="text-lg font-bold text-left">다가오는 기념일</h3>
          <button 
            onClick={() => setShowAllAnniversaries(true)}
            className="text-[10px] font-black text-gray-300 flex items-center gap-0.5 uppercase tracking-wider hover:text-primary transition-colors cursor-pointer"
          >
            전체보기 <ChevronRight size={12} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {upcomingAnniversaries.map((anniversary, index) => (
            <motion.div 
              key={`${dDayMode}-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-5 rounded-[28px] border border-secondary shadow-sm flex flex-col items-center gap-2 relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-primary/20 group-hover:bg-primary transition-colors" />
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">{anniversary.title}</p>
              <span className={`text-xl font-black ${anniversary.dDay === 0 ? 'text-primary' : 'text-charcoal'}`}>
                {anniversary.dDay === 0 ? 'D-Day' : `D-${anniversary.dDay}`}
              </span>
              <p className="text-[9px] font-bold text-gray-300">{format(anniversary.date, 'yyyy.MM.dd')}</p>
            </motion.div>
          ))}
          {upcomingAnniversaries.length === 0 && (
            <div className="col-span-2 py-8 bg-secondary/30 rounded-3xl flex flex-center justify-center text-gray-300 text-xs font-bold">
              다가오는 기념일이 없습니다.
            </div>
          )}
        </div>
      </section>

      {/* Notification Banner */}
      <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 flex items-center gap-4">
        <div className="bg-white p-2 rounded-full text-primary shadow-sm">
          <Bell size={20} fill="currentColor" />
        </div>
        <div className="text-left">
          <p className="text-sm font-bold text-primary leading-tight">알림</p>
          <p className="text-[11px] text-charcoal/70 mt-0.5 leading-tight">
            기준: {dDayMode === 'met' ? '처음 만난 날' : '사귀기 시작한 날'} ({activeDate})
          </p>
        </div>
      </div>

      {/* Anniversary List Modal */}
      <AnimatePresence>
        {showAllAnniversaries && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAllAnniversaries(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="relative w-full max-w-lg bg-white rounded-t-[40px] p-8 shadow-2xl h-[80vh] flex flex-col"
            >
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary/10 rounded-2xl text-primary">
                    <Calendar size={20} />
                  </div>
                  <h2 className="text-xl font-black text-charcoal">기념일 목록</h2>
                </div>
                <button 
                  onClick={() => setShowAllAnniversaries(false)} 
                  className="bg-secondary p-2 rounded-full text-gray-400 hover:text-charcoal transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                <div className="flex flex-col gap-3">
                  {allAnniversaries.map((anniversary, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-center justify-between p-5 bg-secondary/30 rounded-3xl hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xs ${anniversary.dDay === 0 ? 'bg-primary text-white shadow-lg shadow-primary/30' : 'bg-white text-gray-400 border border-secondary'}`}>
                          {anniversary.title.includes('주년') ? 'YEAR' : 'DAY'}
                        </div>
                        <div className="text-left">
                          <p className="font-black text-charcoal text-sm">{anniversary.title} 기념일</p>
                          <p className="text-[11px] text-gray-400 font-bold mt-0.5">{format(anniversary.date, 'yyyy년 MM월 dd일')}</p>
                        </div>
                      </div>
                      <span className={`font-black text-sm px-4 py-2 rounded-2xl ${anniversary.dDay === 0 ? 'bg-primary text-white' : 'bg-white text-primary border border-primary/10'}`}>
                        {anniversary.dDay === 0 ? 'Today' : `D-${anniversary.dDay}`}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {isEditing && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditing(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">환경 설정</h2>
                <button onClick={() => setIsEditing(false)} className="text-gray-400 hover:text-charcoal transition-colors p-1">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 ml-1 uppercase">나</label>
                    <input 
                      type="text" 
                      value={tempInfo.name1}
                      onChange={(e) => setTempInfo({...tempInfo, name1: e.target.value})}
                      className="w-full bg-secondary border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 ml-1 uppercase">상대방</label>
                    <input 
                      type="text" 
                      value={tempInfo.name2}
                      onChange={(e) => setTempInfo({...tempInfo, name2: e.target.value})}
                      className="w-full bg-secondary border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 ml-1 uppercase">처음 만난 날</label>
                    <input 
                      type="date" 
                      value={tempInfo.firstMetDate}
                      onChange={(e) => setTempInfo({...tempInfo, firstMetDate: e.target.value})}
                      className="w-full bg-secondary border-none rounded-xl px-4 py-3 text-xs focus:ring-2 focus:ring-primary outline-none transition-all font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 ml-1 uppercase">사귀기 시작한 날</label>
                    <input 
                      type="date" 
                      value={tempInfo.anniversaryDate}
                      onChange={(e) => setTempInfo({...tempInfo, anniversaryDate: e.target.value})}
                      className="w-full bg-secondary border-none rounded-xl px-4 py-3 text-xs focus:ring-2 focus:ring-primary outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 mb-1 ml-1 uppercase">기본 표시 기준</label>
                  <div className="flex bg-secondary p-1 rounded-xl">
                    <button 
                      onClick={() => setTempInfo({...tempInfo, baseDateType: 'met'})}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${tempInfo.baseDateType === 'met' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
                    >
                      만난 날
                    </button>
                    <button 
                      onClick={() => setTempInfo({...tempInfo, baseDateType: 'dated'})}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${tempInfo.baseDateType === 'dated' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
                    >
                      사귄 날
                    </button>
                  </div>
                </div>

                <button 
                  onClick={handleSave}
                  className="w-full bg-primary text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/30 active:scale-95 transition-all mt-4"
                >
                  설정 저장하기
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomeScreen;
